/** One-off persisted-tab path rewrites for workspace-store schema migrations. */
import { ATTENTION_ROUTES } from '@shared/constants/routes';
import { settingsTarget } from '@shared/navigation/settingsRedirects';

import type { WorkspaceTab } from '../types';

/**
 * Rewrite tabs left over from before the nutrition tracker moved from
 * `/meals/nutrition` to the top-level `/food-diary` route. Matches on the
 * pathname so a persisted query/hash suffix still migrates, and refreshes the
 * tab title so it no longer reads the stale "Nutrition"/"Meals".
 */
export function rewriteNutritionTabs(tabs: WorkspaceTab[]): WorkspaceTab[] {
  return tabs.map(tab =>
    tab.path.split(/[?#]/)[0] === '/meals/nutrition'
      ? { ...tab, path: '/food-diary', title: 'Food Diary' }
      : tab,
  );
}

/** Exact pre-rename path → its attention-surface replacement. */
const ATTENTION_PATH_REWRITES: Record<string, string> = {
  '/inbox': ATTENTION_ROUTES.root,
  '/email': ATTENTION_ROUTES.root,
  '/inbox/history': ATTENTION_ROUTES.history,
  // Two generations of the same tab: the pre-attention `/later`, and the
  // `/attention/later` that replaced it before the Reading Room rename. Both
  // land on the current route — a user who never reopened the tab in between
  // still has the older path persisted.
  '/later': ATTENTION_ROUTES.readingRoom,
  '/attention/later': ATTENTION_ROUTES.readingRoom,
  '/email/starred': ATTENTION_ROUTES.starred,
  '/email/all': ATTENTION_ROUTES.mailAll,
  '/email/sent': ATTENTION_ROUTES.mailSent,
  '/email/drafts': ATTENTION_ROUTES.mailDrafts,
  '/email/labels': ATTENTION_ROUTES.mailLabels,
  '/email/spam': ATTENTION_ROUTES.mailSpam,
  // The Email Actions page is gone; its rules live in Mail settings now.
  // Both generations of the tab redirect there rather than being dropped.
  '/email/actions': ATTENTION_ROUTES.mailSettings,
  '/attention/mail/actions': ATTENTION_ROUTES.mailSettings,
};

/** Message deep links keep their id, so they rewrite by prefix rather than exact match. */
const MESSAGE_PREFIXES = ['/inbox/message/', '/email/message/'];
const MESSAGE_TARGET_PREFIX = ATTENTION_ROUTES.message.replace(':id', '');

/**
 * Rewrite tabs left over from before the inbox and email views were unified
 * onto the `/attention` surface. Without this, every tab a user had open
 * resolves to nothing and the side pane shows "not available" — the rename
 * would silently eat their workspace.
 *
 * Matches on the pathname so a persisted query/hash suffix still migrates.
 *
 * Titles are left alone for the `/attention` unification — they were already
 * the view names ("Inbox", "Sent"), which that rename did not change. The
 * Reading Room is the exception: it renamed the *view*, so a tab still
 * captioned "Later" would keep a retired name alive on screen indefinitely.
 */
export function rewriteAttentionTabs(tabs: WorkspaceTab[]): WorkspaceTab[] {
  return tabs.map(tab => {
    const [pathname, suffix] = splitSuffix(tab.path);

    const exact = ATTENTION_PATH_REWRITES[pathname];
    if (exact) return { ...tab, path: `${exact}${suffix}`, ...retitle(exact) };

    const prefix = MESSAGE_PREFIXES.find(p => pathname.startsWith(p));
    if (prefix) {
      const id = pathname.slice(prefix.length);
      return { ...tab, path: `${MESSAGE_TARGET_PREFIX}${id}${suffix}` };
    }

    return tab;
  });
}

/**
 * The new title for a rewritten path, when the rename changed the view's name.
 *
 * Returns an empty object otherwise, so it spreads into the tab as a no-op
 * rather than clobbering a title the user's tab already had right.
 */
function retitle(newPath: string): { title?: string } {
  return RETITLED[newPath] ? { title: RETITLED[newPath] } : {};
}

/**
 * Paths whose rewrite also changed what the view is called.
 *
 * The Reading Room renamed the view; Mail settings *replaced* a deleted one. In
 * both cases a tab keeping its old caption would name something that no longer
 * exists.
 */
const RETITLED: Record<string, string> = {
  [ATTENTION_ROUTES.readingRoom]: 'Reading Room',
  [ATTENTION_ROUTES.mailSettings]: 'Mail settings',
};

/**
 * Rewrite tabs left over from before Profile, the Assistant and Administration
 * hubs, `/cost` and the `/my-data/*` configuration pages were absorbed into the
 * one Settings page at `/settings/<section>/<item>` (nav spec §4.6).
 *
 * The mapping is `settingsTarget` (`@shared/navigation/settingsRedirects`),
 * kept only for this migration now that the legacy routes no longer redirect. It carries the query
 * suffix itself (`/profile`'s `tab` becomes part of the path), so the returned
 * string is the whole new path.
 *
 * Every rewritten tab is retitled: the catalog names all of `/settings/*`
 * "Settings", except Memories, which stays its own destination at its new
 * address, and the reminder settings, which later left Settings for the
 * Agenda's Settings tab. A tab the mapping does not cover is returned as the
 * same object.
 */
export function rewriteSettingsTabs(tabs: WorkspaceTab[]): WorkspaceTab[] {
  return tabs.map(tab => {
    const [pathname, suffix] = splitSuffix(tab.path);
    const target = settingsTarget(pathname, suffix);
    if (target === undefined) return tab;
    return { ...tab, path: target, title: settingsTabTitle(target) };
  });
}

/** The title a tab rewritten onto `target` wears. */
function settingsTabTitle(target: string): string {
  if (target.startsWith('/settings/assistant/memories')) return 'Memories';
  if (target.startsWith('/agenda')) return 'Agenda';
  return 'Settings';
}

/**
 * Rewrite tabs left over from the standalone New chat page. `/chat/new` was
 * removed when the conversation page grew its own no-messages state; without
 * this, a persisted tab resolves to nothing and the pane shows "not available".
 *
 * Matches on the pathname so a persisted query/hash suffix still migrates, and
 * retitles, since "New chat" no longer names a place.
 */
export function rewriteChatNewTabs(tabs: WorkspaceTab[]): WorkspaceTab[] {
  return tabs.map(tab => {
    const [pathname, suffix] = splitSuffix(tab.path);
    return pathname === '/chat/new' ? { ...tab, path: `/chat${suffix}`, title: 'Chat' } : tab;
  });
}

/** Split a stored path into its pathname and any `?query`/`#hash` suffix. */
function splitSuffix(path: string): [string, string] {
  const match = /[?#]/.exec(path);
  return match ? [path.slice(0, match.index), path.slice(match.index)] : [path, ''];
}
